import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Bot, Loader2 } from 'lucide-react';

const AI_ENDPOINT = 'https://aaairrigationservice.com/api/ai/chat';

export const BoatmanAI = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { type: 'ai', text: "Hi! I'm Flowbot, an AI built exclusively for AAA Irrigation Service to help you today. I'm here to answer your sprinkler questions and get you scheduled." }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open, loading]);

  const sendMessage = async () => {
    const query = input.trim();
    if (!query || loading) return;

    // Snapshot current messages before adding new one
    const currentMessages = messages;
    setMessages(prev => [...prev, { type: 'user', text: query }]);
    setInput('');
    setLoading(true);

    // Build history for backend: convert all messages except the initial greeting
    const history = currentMessages.slice(1).map(m => ({
      role: m.type === 'user' ? 'user' : 'assistant',
      content: m.text,
    }));

    try {
      const res = await fetch(AI_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: query, history }),
      });
      const data = await res.json();
      setMessages(prev => [...prev, { type: 'ai', text: data.response || "I'm having trouble right now. Please call us at (469) 751-3567 for immediate help." }]);
    } catch {
      setMessages(prev => [...prev, { type: 'ai', text: "Connection issue. Please call us directly at (469) 751-3567." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
      {/* Chat Window */}
      {open && (
        <div
          className="w-80 sm:w-96 bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
          style={{ height: '440px' }}
        >
          {/* Header */}
          <div className="bg-brand-blue px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <Bot className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-white font-semibold text-sm">Boatman AI™</p>
                <div className="flex items-center gap-1">
                  <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></div>
                  <p className="text-blue-100 text-xs">Online</p>
                </div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-white/80 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${
                  msg.type === 'user'
                    ? 'bg-brand-blue text-white rounded-br-sm'
                    : 'bg-white text-slate-700 shadow-sm border border-slate-200 rounded-bl-sm'
                }`}>
                  {msg.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-3 py-2 shadow-sm">
                  <Loader2 className="w-4 h-4 text-brand-blue animate-spin" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 bg-white border-t border-slate-200">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && sendMessage()}
                placeholder="Ask about your irrigation system..."
                className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue outline-none transition-colors"
              />
              <button
                onClick={sendMessage}
                disabled={loading || !input.trim()}
                className="w-9 h-9 bg-brand-blue hover:bg-brand-blue-dark disabled:opacity-40 text-white rounded-xl flex items-center justify-center transition-colors flex-shrink-0"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <p className="text-center text-xs text-slate-400 mt-1 leading-relaxed">
              By sharing your number, you consent to SMS from <strong>AAA Irrigation Service</strong>.
              Msg &amp; data rates may apply. Msg frequency varies. Reply <strong>STOP</strong> to opt out.{' '}
              <a href="/privacy" className="underline hover:text-slate-600">Privacy</a>
            </p>
            <p className="text-center text-xs text-slate-400 mt-1">Powered by Boatman Systems™</p>
          </div>
        </div>
      )}

      {/* Toggle Button */}
      {open ? (
        <button
          onClick={() => setOpen(false)}
          className="w-10 h-10 bg-brand-blue hover:bg-brand-blue-dark text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300"
          aria-label="Close chat"
        >
          <X className="w-5 h-5" />
        </button>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
          <style>{`
            @keyframes danceCaption {
              0%   { transform: translateY(0) rotate(0deg); }
              15%  { transform: translateY(-6px) rotate(-4deg); }
              30%  { transform: translateY(0) rotate(4deg); }
              45%  { transform: translateY(-4px) rotate(-2deg); }
              60%  { transform: translateY(0) rotate(2deg); }
              75%  { transform: translateY(-2px) rotate(0deg); }
              100% { transform: translateY(0) rotate(0deg); }
            }
          `}</style>
          <div style={{
            background: '#1b7abf',
            color: '#fff',
            fontSize: 14,
            fontWeight: 700,
            padding: '6px 16px',
            borderRadius: 12,
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            position: 'relative',
            animation: 'danceCaption 1.4s ease-in-out 3',
            display: 'inline-block',
          }}>
            💧 Got Sprinkler Problems?
            <div style={{
              position: 'absolute',
              bottom: -7,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '7px solid transparent',
              borderRight: '7px solid transparent',
              borderTop: '7px solid #1b7abf',
            }} />
          </div>
          <button
            onClick={() => setOpen(true)}
            aria-label="Open Boatman AI chat"
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
          >
            <img
              src="/flowbot.webp"
              alt="Flow Bot™"
              style={{
                width: 160,
                height: 160,
                objectFit: 'cover',
                objectPosition: 'top',
                borderRadius: '50%',
                boxShadow: '0 4px 20px rgba(27,122,191,0.5)',
              }}
            />
          </button>
        </div>
      )}
    </div>
  );
};
